import React, { useState } from "react";
import { Upload, Cpu, FileCheck2, Check } from "lucide-react";
import { useReveal, useCountUp, eur } from "./landingUtils";

const PASOS = [
  [Upload, "Sube tu inventario y tus ventas", "Excel, TXT o CSV directo de tu programa de gestión. Detectamos el formato solo, también las ventas por empleado y por ticket."],
  [Cpu, "Farmanaliza lo clasifica", "ABCD en 7 niveles, stock D real con fecha de entrada, rotación a PVP, sustitutivos agrupados con IA y consejo por empleado."],
  [FileCheck2, "Recibes el plan de acción", "Cuadro de mando, lista de productos a recomendar, pedidos a proveedor e informes PDF para cada persona del equipo."],
];

export function Pasos() {
  const [ref, cls] = useReveal();
  return (
    <section id="como-funciona" className="py-24 sm:py-32 bg-white" data-testid="landing-pasos">
      <div ref={ref} className={"max-w-7xl mx-auto px-5 sm:px-8 " + cls}>
        <div className="font-mono text-xs font-semibold uppercase tracking-widest text-[#1f6fd1]">Cómo funciona</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#12305e] mt-3 max-w-2xl">Tres pasos, un informe que se entiende.</h2>
        <div className="grid md:grid-cols-3 gap-px bg-slate-200 mt-16 rounded-2xl overflow-hidden border border-slate-200">
          {PASOS.map(([I, t, d], i) => (
            <div key={t} className="group bg-white p-8 lg:p-10 hover:bg-[#f5f9ff] transition-colors" data-testid={`paso-${i + 1}`} style={{ transitionDelay: `${i * 90}ms` }}>
              <div className="flex items-center justify-between">
                <span className="font-display text-6xl font-extrabold text-slate-100 group-hover:text-[#1f6fd1]/15 transition-colors">0{i + 1}</span>
                <span className="h-12 w-12 rounded-xl bg-[#12305e] text-white flex items-center justify-center group-hover:-translate-y-1 transition-transform"><I size={22} /></span>
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900 mt-8">{t}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm sm:text-base">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Slider({ label, value, set, min, max, step, fmt, testid }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="font-display text-2xl font-extrabold text-[#12305e]" data-testid={`${testid}-value`}>{fmt(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => set(Number(e.target.value))}
             className="w-full mt-3 accent-[#1f6fd1] h-2 cursor-pointer" data-testid={testid} />
      <div className="flex justify-between text-[11px] text-slate-400 font-mono mt-1"><span>{fmt(min)}</span><span>{fmt(max)}</span></div>
    </label>
  );
}

export function Calculadora() {
  const [stock, setStock] = useState(180000);
  const [pct, setPct] = useState(12);
  const [rec, setRec] = useState(50);
  const parado = stock * pct / 100;
  const recuperable = parado * rec / 100;
  const vParado = useCountUp(parado), vRec = useCountUp(recuperable);
  const [ref, cls] = useReveal();
  return (
    <section id="calculadora" className="py-24 sm:py-32 bg-[#f5f8fc] relative overflow-hidden" data-testid="landing-calculadora">
      <div className="absolute -right-40 top-20 h-[480px] w-[480px] rounded-full bg-[#1f6fd1]/10 blur-3xl pointer-events-none" />
      <div ref={ref} className={"relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center " + cls}>
        <div>
          <div className="font-mono text-xs font-semibold uppercase tracking-widest text-[#1f6fd1]">Calcula el tuyo</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#12305e] mt-3">¿Cuánto dinero tienes dormido en las baldas?</h2>
          <p className="text-slate-600 mt-5 text-base md:text-lg leading-relaxed">Mueve los controles con los datos de tu farmacia. Es tu propia estimación: Farmanaliza te da la cifra exacta, producto a producto, con tu fichero.</p>
          <div className="relative mt-10 rounded-2xl overflow-hidden shadow-2xl shadow-[#12305e]/20 hidden lg:block">
            <img src="/img/farmanaliza-inventario.jpg?v=2" alt="Farmacéutica revisando inventario" className="w-full aspect-[3/2] object-cover" />
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/60 p-7 sm:p-10" data-testid="calc-card">
          <div className="space-y-8">
            <Slider label="Valor de tu stock a PVP" value={stock} set={setStock} min={40000} max={800000} step={5000} fmt={eur} testid="calc-stock" />
            <Slider label="Stock sin ventas en el último año" value={pct} set={setPct} min={2} max={40} step={1} fmt={(v) => `${v} %`} testid="calc-pct" />
            <Slider label="Parte que crees que puedes recuperar" value={rec} set={setRec} min={10} max={90} step={5} fmt={(v) => `${v} %`} testid="calc-rec" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Dinero parado</div>
              <div className="font-display text-3xl font-extrabold text-red-600 mt-2 tabular-nums" data-testid="calc-parado">{eur(vParado)}</div>
            </div>
            <div className="rounded-2xl bg-[#12305e] text-white p-5 relative overflow-hidden">
              <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[#1f6fd1]/40 blur-xl" />
              <div className="relative text-xs font-semibold uppercase tracking-widest text-blue-200">Podrías recuperar</div>
              <div className="relative font-display text-3xl font-extrabold mt-2 tabular-nums" data-testid="calc-recuperable">{eur(vRec)}</div>
            </div>
          </div>
          <ul className="mt-8 space-y-2 text-sm text-slate-600">
            {["Devolución o traspaso a laboratorio de lo que no rota", "Lista de D real para que tu equipo lo recomiende con incentivo", "Menos compra de lo que ya tienes: mínimos y máximos por venta real"].map((t) => (
              <li key={t} className="flex gap-2"><Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />{t}</li>
            ))}
          </ul>
          <a href="#contacto" className="mt-8 w-full inline-flex justify-center items-center px-6 py-3.5 rounded-full bg-[#1f6fd1] text-white font-bold hover:bg-[#1658a8] transition-colors" data-testid="calc-cta">Quiero mi análisis</a>
          <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">Cálculo orientativo: valor del stock × % sin ventas × % recuperable. No usa datos de otras farmacias.</p>
        </div>
      </div>
    </section>
  );
}
